/**
 * Word Lists Page
 * List and manage word lists
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useViewMode } from '@/contexts/ViewModeContext';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Input } from '@/components/common/Input';
import { ViewModeToggle } from '@/components/common/ViewModeToggle';
import { apiRequest } from '@/api/client';

interface WordList {
  wordListId: string;
  name: string;
  className: string;
  classId: string;
  teacherName?: string;
  words: string[];
  isActive: boolean;
  createdAt: string;
}

export const WordListsPage = () => {
  const { user } = useAuth();
  const { viewMode } = useViewMode();
  const navigate = useNavigate();
  const [wordLists, setWordLists] = useState<WordList[]>([]);
  const [filteredWordLists, setFilteredWordLists] = useState<WordList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchWordLists();
  }, [viewMode]);

  useEffect(() => {
    if (searchTerm) {
      setFilteredWordLists(
        wordLists.filter(wl =>
          wl.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          wl.className.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredWordLists(wordLists);
    }
  }, [searchTerm, wordLists]);

  const fetchWordLists = async () => {
    try {
      setIsLoading(true);
      
      // First get classes
      let classesResponse;
      if (user?.isAdmin && viewMode === 'school') {
        classesResponse = await apiRequest<{ classes: any[] }>('get_school_classes', {
          schoolId: user?.schoolId,
        });
      } else {
        classesResponse = await apiRequest<{ classes: any[] }>('get_teacher_classes', {
          userEmail: user?.email,
        });
      }
      
      if (import.meta.env.DEV) {
        console.log('Classes response:', classesResponse);
      }
      
      const classes = classesResponse.success && (classesResponse as any).classes 
        ? (classesResponse as any).classes 
        : [];
      
      if (import.meta.env.DEV) {
        console.log('Classes found:', classes.length);
      }
      
      // Aggregate word lists from all classes
      const allWordLists: WordList[] = [];
      
      for (const classItem of classes) {
        try {
          if (import.meta.env.DEV) {
            console.log('Fetching word lists for class:', classItem.classId, classItem.className);
          }
          
          const wordListsResponse = await apiRequest<{ wordLists: WordList[] }>('get_class_wordlists', {
            classId: classItem.classId,
          });
          
          if (import.meta.env.DEV) {
            console.log('Word lists response for', classItem.className, ':', wordListsResponse);
          }
          
          // Backend returns 'wordlists' (lowercase), not 'wordLists'
          const wordListsData = (wordListsResponse as any).wordlists || (wordListsResponse as any).wordLists;
          
          if (wordListsResponse.success && wordListsData) {
            const classWordLists = wordListsData.map((wl: any) => ({
              ...wl,
              className: classItem.className,
              classId: classItem.classId,
              teacherName: classItem.teacherName,
            }));
            if (import.meta.env.DEV) {
              console.log('Adding word lists:', classWordLists.length);
            }
            allWordLists.push(...classWordLists);
          }
        } catch (error) {
          console.error(`Failed to fetch word lists for class ${classItem.classId}:`, error);
        }
      }
      
      if (import.meta.env.DEV) {
        console.log('Total word lists found:', allWordLists.length);
      }
      
      setWordLists(allWordLists);
      setFilteredWordLists(allWordLists);
    } catch (error) {
      console.error('Failed to fetch word lists:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Word Lists</h1>
          <p className="text-slate-600 mt-2">Manage vocabulary words for your classes</p>
        </div>
        <div className="flex items-center gap-3">
          <ViewModeToggle />
          <Button variant="primary" onClick={() => navigate('/wordlists/new')}>
            ➕ Create Word List
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <div className="p-4">
          <Input
            type="text"
            placeholder="Search word lists by name or class..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </Card>

      {/* Word Lists Grid */}
      {filteredWordLists.length === 0 ? (
        <Card>
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📝</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {searchTerm ? 'No word lists found' : 'No word lists yet'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchTerm
                ? 'Try adjusting your search'
                : 'Create word lists to add vocabulary words for your classes'}
            </p>
            {!searchTerm && (
              <Button variant="primary" onClick={() => navigate('/wordlists/new')}>
                Create Your First Word List
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWordLists.map((wordList) => (
            <Card key={wordList.wordListId}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-slate-900">
                        {wordList.name}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        (wordList as any).listType === 'phonics' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {(wordList as any).listType === 'phonics' ? 'Sounds' : 'Words'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{wordList.className}</p>
                    {user?.isAdmin && viewMode === 'school' && wordList.teacherName && (
                      <p className="text-xs text-slate-500 mt-1">
                        Teacher: {wordList.teacherName}
                      </p>
                    )}
                  </div>
                  {wordList.isActive && (
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg">
                      Active
                    </span>
                  )}
                </div>

                <div className="mb-4">
                  <p className="text-sm text-slate-600 mb-2">
                    {wordList.words.length} {(wordList as any).listType === 'phonics' ? 'sound' : 'word'}{wordList.words.length !== 1 ? 's' : ''}
                  </p>
                  {wordList.words.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-sm text-slate-700 line-clamp-2">
                        {wordList.words.slice(0, 5).join(', ')}
                        {wordList.words.length > 5 && '...'}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/wordlists/${wordList.wordListId}`)}
                    className="flex-1"
                  >
                    View
                  </Button>
                  {!wordList.isActive && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {/* TODO: Set as active */}}
                      className="flex-1"
                    >
                      Set Active
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
